import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { connect } from 'react-redux';

import {
  addPageData,
  navigateWizard,
  addLiability,
  removeLiability,
  persistLiabilities,
} from '../../actions';
import Button from '../../components/Button';
import ItemBox from '../../components/ItemBox/ItemBox';
import LoanInputCard from '../../components/LoanInputCard';
import PageLayout from '../../components/PageLayout/PageLayout';
import analytics from '../../lib/analytics';
import { shortMonth } from '../../lib/dateFormatter';
import useDefaultCurrency from '../../lib/hooks/useDefaultCurrency';
import { currencyFormattedNumber } from '../../lib/numberFormatter';
import * as payloadFactory from '../../lib/pageDataPayloadFactory';

import { liabilityKey, liabilityTitleText } from './HasDebtHelpers';

import './index.scss';

export const HasDebtPage = ({
  liabilities,
  dispatchAddLiability,
  dispatchRemoveLiability,
  dispatchPersistLiabilities,
  dispatchAddPageData,
  dispatchWizardNav,
}) => {
  const [showLoanCard, setShowLoanCard] = useState(false);
  const [defaultCurrency, currencies] = useDefaultCurrency();

  const handleAddLoan = () => {
    setShowLoanCard(true);
  };

  const handleLoanUpdate = payload => {
    dispatchAddLiability(payload);
    setShowLoanCard(false);
  };

  const handleCancel = () => {
    setShowLoanCard(false);
  };

  const handleLoanDelete = event => {
    dispatchRemoveLiability(event.currentTarget.id);
  };

  const handleSubmit = event => {
    event.preventDefault();

    analytics.sendEvent('Click - Next', 'has-debt');

    const hasDebt = liabilities.length > 0;
    dispatchAddPageData(payloadFactory.createAddPageDataPayload('hasDebt', hasDebt));
    if (!hasDebt) {
      const clearLiabilitiesPayload = payloadFactory.createAddPageDataPayload('liabilities', []);
      dispatchAddPageData(clearLiabilitiesPayload);
    }

    dispatchPersistLiabilities();
    dispatchWizardNav({ dir: 'fwd' });
  };

  return (
    <PageLayout title="Do you have any outstanding loans in your name?">
      <div className="row-spacer">
        Tell us about any loans that you have not fully repaid and would show up on your credit
        report.
      </div>

      {liabilities.length === 0 && !showLoanCard && (
        <div className="row">
          <div className="col col--12/16-large">
            <div className="no-loans-box">No outstanding loans</div>
          </div>
        </div>
      )}
      {showLoanCard && (
        <div className="row">
          <div className="col col--12/16-large">
            <LoanInputCard
              addLoan={handleLoanUpdate}
              cancelLoanInput={handleCancel}
              currencies={currencies}
              defaultCurrency={defaultCurrency}
            />
          </div>
        </div>
      )}
      {liabilities.length > 0 && !showLoanCard && (
        <div className="row">
          <div className="col col--12/16-large">
            {liabilities.map(liability => (
              <ItemBox
                key={liabilityKey(liability)}
                ariaLabel="liability item"
                displayButton
                buttonHandler={handleLoanDelete}
                buttonId={liabilityKey(liability)}
                title={liabilityTitleText(liability.liabilityType)}
                body={`${currencyFormattedNumber(liability.currency, liability.amount)} at ${
                  liability.rate
                }%\nuntil ${shortMonth(
                  new Date(liability.lastRepaymentYear, liability.lastRepaymentMonth),
                )}`}
              />
            ))}
          </div>
        </div>
      )}

      {!showLoanCard && (
        <div className="add-loan-link" onClick={handleAddLoan}>
          {liabilities.length === 0 ? '+ Add a loan' : '+ Add another loan'}
        </div>
      )}
      <div className="row-spacer">
        <Button type="submit" onClick={handleSubmit} isDisabled={showLoanCard}>
          Next
        </Button>
      </div>
    </PageLayout>
  );
};

HasDebtPage.propTypes = {
  dispatchWizardNav: PropTypes.func.isRequired,
  dispatchAddPageData: PropTypes.func.isRequired,
  dispatchAddLiability: PropTypes.func.isRequired,
  dispatchRemoveLiability: PropTypes.func.isRequired,
  dispatchPersistLiabilities: PropTypes.func.isRequired,
  liabilities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      liabilityType: PropTypes.string.isRequired,
      currency: PropTypes.string.isRequired,
      amount: PropTypes.string.isRequired,
      term: PropTypes.string.isRequired,
      rate: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

HasDebtPage.defaultProps = {
  initialHasDebt: null,
};

export const mapStateToProps = reduxState => ({
  initialHasDebt: reduxState.builder.ui.hasDebt,
  liabilities: reduxState.builder.ui.liabilities,
});

export const mapDispatchToProps = {
  dispatchAddPageData: addPageData,
  dispatchWizardNav: navigateWizard,
  dispatchAddLiability: addLiability,
  dispatchRemoveLiability: removeLiability,
  dispatchPersistLiabilities: persistLiabilities,
};

export default connect(mapStateToProps, mapDispatchToProps)(HasDebtPage);
